import React, { useEffect, useState } from "react";
import {
  verificationDetailsValidationSchema,
  memberVerificationDetailsValidationSchema,
} from "../../utils/validationSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "../../components/ui";
import FormInput from "../../components/ui/FormInput";
import {
  verificationDetailsFields,
  handleFileChange,
  memberVerificationDetailsFields,
} from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "../../components/shared/ErrorMessage";

const VerificationDetails = ({
  customerId,
  verificationDetailsData,
  setVerificationDetailsData,
  handleNext,
  type,
  // Disbursed loan: fields are already read-only via type="View"; accepted
  // here so the prop is declared rather than leaking to the DOM.
  locked = false,
  mode,
}) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm({
    resolver: yupResolver(
      mode === "customer"
        ? verificationDetailsValidationSchema
        : memberVerificationDetailsValidationSchema
    ),
    defaultValues: verificationDetailsData,
  });

  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const [aadharList, setAadharList] = useState([]);
  const [aadharNo, setAadharNo] = useState("");
  const [aadharMessage, setAadharMessage] = useState("");

  const formatAadhar = (rawAadhar) => {
    if (!rawAadhar) return "";
    const clean = String(rawAadhar).replace(/\D/g, "");
    const digits = clean.substring(0, 12);
    let formatted = "";
    if (digits.length > 0) {
      formatted += digits.substring(0, 4);
    }
    if (digits.length > 4) {
      formatted += "-" + digits.substring(4, 8);
    }
    if (digits.length > 8) {
      formatted += "-" + digits.substring(8, 12);
    }
    return formatted;
  };

  useEffect(() => {
    if (verificationDetailsData) {
      Object.keys(verificationDetailsData).forEach((key) => {
        setValue(key, verificationDetailsData[key]);

        if (key === "aadharNo" && verificationDetailsData[key]) {
          setAadharNo(formatAadhar(verificationDetailsData[key]));
        }

        // Check if the field corresponds to an image
        if (key.endsWith("Image") && verificationDetailsData[key]) {
          setExistingFiles((prev) => ({
            ...prev,
            [key]: `${API_BASE_URL}/images/${verificationDetailsData[key]}`, // Construct the image URL
          }));
        }
      });
    }
  }, [verificationDetailsData, setValue]);

  useEffect(() => {
    if (type === "View") return;
    const getaadharcustomer = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/getModelData/verification_details?fields=aadharNo`
        );
        if (response.data && Array.isArray(response.data.data)) {
          const aadharNumbers = response.data.data.map((item) =>
            String(item.aadharNo)
          );
          setAadharList(aadharNumbers);
        }
      } catch (error) {
        console.error("Error fetching Aadhar details:", error);
      }
    };

    getaadharcustomer();
  }, [type]);

  useEffect(() => {
    if (aadharMessage) {
      const timer = setTimeout(() => {
        setAadharMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [aadharMessage]);

  const handleAadharChange = (e) => {
    const value = e.target.value;
    const clean = value.replace(/\D/g, "");
    const digits = clean.substring(0, 12);

    setValue("aadharNo", digits, { shouldValidate: true });

    let formatted = "";
    if (digits.length > 0) {
      formatted += digits.substring(0, 4);
    }
    if (digits.length > 4) {
      formatted += "-" + digits.substring(4, 8);
    }
    if (digits.length > 8) {
      formatted += "-" + digits.substring(8, 12);
    }
    setAadharNo(formatted);

    if (digits.length === 12) {
      if (aadharList.includes(digits)) {
        setAadharMessage("taken");
      } else {
        setAadharMessage("available");
      }
    } else {
      setAadharMessage("");
    }
  };

  const onSubmit = async (data) => {
    // Aadhaar Uniqueness Check
    if (aadharMessage === "taken") {
      setErrorMsg("Aadhaar number is already registered in the system.");
      return;
    }
    console.log("Form Data:", JSON.stringify(data));

    const formData = new FormData();

    // ALWAYS sent, not just in Add. customerId says which customer this row
    // belongs to, and it is not a form field — so restricting it to Add meant
    // every other save reached the server without it. The server mints the
    // customer's unique ID before writing, refuses without a customerId, and the
    // whole transaction rolls back — which surfaced as a bare 500 and lost the
    // entire submission.
    if (!customerId) {
      setErrorMsg("No customer selected, so these details cannot be saved.");
      return;
    }
    formData.append("customerId", customerId);

    Object.entries(data).forEach(([key, value]) => {
      // Database bookkeeping, never user input. The form is populated from the
      // saved row, so `id`, `createdAt` and `updatedAt` come back as form values
      // and were being posted as though they had been typed — `id` being the
      // row's own primary key.
      //
      // `id` is put BACK for Edit further down: that endpoint locates the row by
      // it. Stripping it here without restoring it there is what broke Update.
      if (key === "id" || key === "customerId" || key === "createdAt" || key === "updatedAt") {
        return;
      }

      if (filePreviews[key]?.file) {
        formData.append(key, filePreviews[key].file);
      } else {
        // Append other non-file fields
        if (value !== null && value !== "") {
          // DIGITS ONLY. The field displays 2525-3636-1414, but aadharNo is a
          // bigint column — MySQL keeps the digits before the first dash and
          // discards the rest, storing 2525 with no error and no warning. The
          // Aadhaar is destroyed silently. PersonalDetails.js already strips
          // these before sending; this path did not.
          const outgoing =
            key === "aadharNo" ? String(value).replace(/\D/g, "") : value;
          if (outgoing !== "") {
            formData.append(key, outgoing);
          }
        }
      }
    });

    let apiName;
    if (type === "Edit") {
      // `id` is appended HERE, not left to the loop above — the loop strips it
      // along with createdAt/updatedAt, which is right for the Add endpoint but
      // wrong for this one.
      //
      // The two paths identify the row differently:
      //   Add  -> addVerificationDetailsWithImages, upserts on customerId
      //   Edit -> addDetailsWithImages, findOne({ where: { id } })
      //
      // Without it the server answered
      //   "WHERE parameter \"id\" has invalid \"undefined\" value"
      // and the update was lost. Taken from the loaded record rather than the
      // form, since the form's copy is exactly what the strip removes.
      const rowId = verificationDetailsData?.id ?? data.id;
      if (!rowId) {
        setErrorMsg(
          "These verification details have not been saved yet, so there is nothing to update."
        );
        return;
      }
      formData.append("model", "verification_details");
      formData.append("type", type);
      formData.append("id", rowId);
      apiName = "addDetailsWithImages";
    }
    if (type === "Add") {
      apiName = "addVerificationDetailsWithImages";
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/${apiName}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true, // If cookies are needed
        }
      );

      if (response.status === 201) {
        setVerificationDetailsData(response.data.savedData);
        console.log("Verification Details added successfully: ", response.data);
        if (type === "Add") {
          handleNext();
        }
        if (type === "Edit") {
          if (mode === "customer") {
            navigate("/addEditCustomer", {
              state: { type: "View", stepNo: 1, customerId },
            });
          }
          if (mode === "businessLoan") {

            handleNext();
          }
        }
        setErrorMsg("");
      } else {
        setErrorMsg(response.data.error || "Error adding verification details");
      }
    } catch (error) {
      console.error("Error Adding Verification Details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error adding verification details";
      if (mode === "businessLoan" && error.response?.status === 403) {
        handleNext();
        return;
      }
      setErrorMsg(msg);
    }
  };
  const onError = (errors) => {
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message;
      return acc;
    }, {});
    console.log("Form errors:", errorMessages);
  };

  const fields = mode === "customer" ? verificationDetailsFields : memberVerificationDetailsFields;

  const renderFields = (fieldList) => {
    return fieldList.map((field, index) => {
      if (field.name === "aadharNo") {
        return (
          <div key={field.name || index} className="col-span-1">
            <FormInput
              label={field.label}
              type="text"
              name={field.name}
              register={register}
              control={control}
              value={aadharNo}
              onChange={handleAadharChange}
              placeholder="Enter Aadhaar Number"
              readOnly={type === "View" || field.readOnly || false}
              required={field.required}
              errors={
                errors.aadharNo
                  ? errors
                  : aadharMessage === "taken"
                    ? {
                      aadharNo: {
                        message: "Already registered in the system.",
                      },
                    }
                    : null
              }
            />
            {aadharMessage === "available" && (
              <p className="text-xs text-success-500 font-medium mt-1">
                Aadhaar is available.
              </p>
            )}
          </div>
        );
      }
      return (
        <div key={field.name || index} className="col-span-1">
          <FormInput
            label={field.label}
            type={field.type}
            name={field.name}
            register={register}
            watch={watch}
            errors={errors}
            options={field.options || []}
            readOnly={type === "View" || field.readOnly || false}
            placeholder={field.placeholder}
            handleFileChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setValue(field.name, file, { shouldValidate: true });
              }
              handleFileChange(e, field.name, setFilePreviews);
            }}
            existingFileUrl={existingFiles[field.name] || ""}
            filePreview={filePreviews[field.name]?.previewUrl || ""}
            required={field.required}
          />
        </div>
      );
    });
  };

  return (
    <div className="bg-white rounded-xl border border-border-light shadow-sm overflow-hidden p-6 max-w-6xl mx-auto mt-6">
      <div className="flex items-start gap-3.5 mb-6 pb-4 border-b border-border-light">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5.5 h-5.5">
            <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="flex-1">
          <h2 className="text-base font-bold text-ink-900">Verification Details</h2>
          <p className="text-xs text-ink-500 mt-0.5">Upload regulatory documents (Aadhaar, PAN, etc.)</p>
        </div>
        {type === "View" && mode === "customer" && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (mode === "customer") {
                navigate("/addEditCustomer", {
                  state: { type: "Edit", stepNo: 1, customerId },
                });
              }
              if (mode === "businessLoan") {
                navigate("/addEditBusinessLoan", {
                  state: { type: "Edit", stepNo: 1, customerId },
                });
              }
            }}
          >
            Edit Details
          </Button>
        )}
      </div>
      <ErrorMessage message={errorMsg} />
      <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-4">
        {/* Aadhaar Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
          {renderFields(fields.filter((f) => ["aadharNo", "aadharFrontImage", "aadharBackImage"].includes(f.name)))}
        </div>

        {/* Secondary ID Text Fields Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
          {renderFields(fields.filter((f) => ["panNo", "idProof", "idProofNo"].includes(f.name)))}
        </div>

        {/* Secondary ID File Fields Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
          {renderFields(fields.filter((f) => ["idProofFrontImage", "idProofBackImage"].includes(f.name)))}
        </div>

        {/* Address Proof Selection & Number Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
          {renderFields(fields.filter((f) => ["addressProof", "addressProofNo"].includes(f.name)))}
        </div>

        {/* Address Proof File Upload Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
          {renderFields(fields.filter((f) => ["addressProofFrontImage", "addressProofBackImage"].includes(f.name)))}
        </div>

        {!(type === "View") && (
          <div className="flex justify-between items-center mt-8 pt-4 border-t border-border-light">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                if (type === "Edit") {
                  if (mode === "customer") {
                    navigate("/addEditCustomer", {
                      state: { type: "View", stepNo: 1, customerId },
                    });
                  }
                  if (mode === "businessLoan") {
                    navigate("/addEditBusinessLoan", {
                      state: { type: "View", stepNo: 1, customerId },
                    });
                  }
                } else {
                  navigate("/customers");
                }
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {type === "Edit" ? "Update" : "Save & Next"}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default VerificationDetails;
